/**
 * JOB HUNTER › EMPLOYERS — /job-hunter/employers
 *
 * Employers the agent knows, with the evidence that they sponsor visas
 * (posting quotes, curated list, your notes). Watching an employer polls its
 * own careers feed; pausing or blocking stops it. "Find careers feed" probes
 * Greenhouse, Lever, Ashby, Workable, SmartRecruiters and Recruitee.
 */
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Ban, BadgeCheck, Building2, Eye, ExternalLink, Landmark, LoaderCircle, CirclePause, Plus, Quote, Radar, Search, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { JobHunterShell, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { CountryTag, Drawer, EmployerAvatar, Field, HeatBadge, Pill, Segmented, JhSelect } from '@/components/job-hunter/ui';
import { useJobHunter } from '@/lib/job-hunter/store';
import { STATUS_META, flag, fmtDate, timeAgo } from '@/lib/job-hunter/format';
import { jobHunterService, type JhEmployer } from '@/services/job-hunter.service';

type Tab = 'watched' | 'evidence' | 'seen' | 'paused' | 'blocked' | 'all';
type Detail = Awaited<ReturnType<typeof jobHunterService.employer>>;

function AddEmployer({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { meta } = useJobHunter();
  const [f, setF] = useState({ name: '', careersUrl: '', domain: '', country: '', evidence: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await jobHunterService.createEmployer({ ...f, careersUrl: f.careersUrl || undefined, domain: f.domain || undefined, country: f.country || undefined, evidence: f.evidence || undefined });
      toast.success(r.atsDetected ? 'Employer added — its careers feed is now polled' : 'Employer added. Use “Find careers feed” to start polling it.');
      setF({ name: '', careersUrl: '', domain: '', country: '', evidence: '' });
      onSaved(); onClose();
    } catch { /* toasted */ } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Watch an employer" widthClass="max-w-xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Company name" className="sm:col-span-2"><input className="jh-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Bending Spoons" /></Field>
        <Field label="Careers board link" hint="A Greenhouse / Lever / Ashby / Workable / SmartRecruiters / Recruitee link starts polling straight away." className="sm:col-span-2">
          <input className="jh-input" value={f.careersUrl} onChange={(e) => setF({ ...f, careersUrl: e.target.value })} placeholder="https://jobs.lever.co/company" />
        </Field>
        <Field label="Website domain"><input className="jh-input" value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="company.com" /></Field>
        <Field label="Country">
          <JhSelect value={f.country} onChange={(v) => setF({ ...f, country: v })} searchPlaceholder="Search country"
            options={[{ value: '', label: 'Not set' }, ...(meta?.countries ?? []).map((c) => ({ value: c.code, label: c.name, icon: <span className="text-[14px] leading-none">{flag(c.code)}</span>, keywords: c.code }))]} />
        </Field>
        <Field label="Why you think they sponsor (optional)" className="sm:col-span-2"><input className="jh-input" value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} placeholder="e.g. In the IND recognised-sponsor register" /></Field>
      </div>
      <div className="mt-5 flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!f.name.trim()} onClick={save}>Watch employer</Button></div>
    </Modal>
  );
}

function EmployerDrawer({ uid, onClose, onChanged }: { uid: string | null; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => { if (!uid) return; const r = await jobHunterService.employer(uid); setD(r); setNotes(r.employer.notes ?? ''); }, [uid]);
  useEffect(() => { setD(null); load().catch(() => undefined); }, [load]);
  const act = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try { await fn(); toast.success(ok); await load(); onChanged(); } catch { /* toasted */ } finally { setBusy(null); }
  };
  const e = d?.employer;
  return (
    <Drawer open={!!uid} onClose={onClose}>
      {!e ? <div className="grid flex-1 place-items-center"><LoaderCircle className="animate-spin text-primary" size={24} /></div> : (
        <>
          <div className="jh-drawer-head">
            <div className="flex items-start gap-4">
              <EmployerAvatar name={e.name} size={52} />
              <div className="min-w-0 flex-1">
                <h2 className="text-[19px] font-bold tracking-tight">{e.name}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-text-secondary">
                  <CountryTag cc={e.country} />
                  {e.domain && <a className="inline-flex items-center gap-1 text-primary hover:underline" href={`https://${e.domain}`} target="_blank" rel="noreferrer">{e.domain}<ExternalLink size={11} /></a>}
                  <Pill tone={e.status === 'watched' ? 'success' : e.status === 'blocked' ? 'danger' : e.status === 'paused' ? 'warning' : 'neutral'}>{e.status}</Pill>
                  {e.atsType ? <Pill tone="success" icon={<BadgeCheck size={12} />}>{e.atsType} · {e.atsSlug}</Pill> : <Pill>No careers feed yet</Pill>}
                  {e.isAgency && <Pill tone="warning">Agency</Pill>}
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-text-muted hover:bg-surface-alt"><X size={17} /></button>
            </div>
          </div>
          <div className="jh-drawer-body space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="jh-kpi"><div className="jh-kpi-label">Jobs seen</div><div className="jh-kpi-value">{e.jobCount}</div></div>
              <div className="jh-kpi"><div className="jh-kpi-label">Matches</div><div className="jh-kpi-value">{e.proposedCount}</div></div>
              <div className="jh-kpi"><div className="jh-kpi-label">“No sponsor”</div><div className="jh-kpi-value">{e.noSponsorshipFeedback}</div></div>
            </div>
            {e.registers && e.registers.length > 0 && (
              <section className="jh-card jh-card-pad">
                <div className="jh-card-title"><Landmark size={15} className="text-success" />Official sponsor registers</div>
                <div className="mt-3 space-y-2">
                  {e.registers.map((r) => (
                    <div key={`${r.country}-${r.name}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-[12.5px]">
                      <span className="inline-flex min-w-0 items-center gap-2"><span className="text-[16px]">{flag(r.country)}</span><b className="truncate">{r.name}</b></span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {r.route && <span className="jh-chip">{r.route}</span>}
                        {r.approvals ? <span className="jh-chip">{r.approvals} H-1B approvals</span> : null}
                        <Pill tone={r.match === 'exact' ? 'success' : 'warning'} title={r.match === 'exact' ? 'Same name' : 'Matched by name prefix — check it is the same company'}>{r.match === 'exact' ? 'Exact name' : 'Name match'}</Pill>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <section className="jh-card jh-card-pad">
              <div className="jh-card-title"><Quote size={15} className="text-success" />Sponsorship evidence</div>
              <div className="mt-3 space-y-2.5">
                {e.sponsorshipEvidence.length ? [...e.sponsorshipEvidence].reverse().map((ev, i) => (
                  <div key={i} className="rounded-xl border border-border p-3">
                    <div className="flex items-center justify-between gap-2 text-[11.5px] text-text-muted"><Pill tone={ev.type === 'posting' ? 'success' : 'info'}>{ev.type === 'posting' ? 'From a posting' : ev.type === 'register' ? 'Official register' : 'Manual'}</Pill>{fmtDate(ev.at)}</div>
                    {ev.quote && <p className="jh-quote mt-2">“{ev.quote}”</p>}
                  </div>
                )) : <p className="jh-muted">No evidence yet. The agent adds a posting's sponsorship sentence here when it finds one.</p>}
              </div>
            </section>
            <section className="jh-card jh-card-pad">
              <div className="jh-card-title">Careers feeds</div>
              <div className="mt-3 space-y-2">
                {d!.sources.map((s) => <div key={s.sourceUid} className="flex items-center justify-between gap-2 text-[12.5px]"><span className="font-semibold">{s.name}</span><span className="flex items-center gap-2"><HeatBadge heat={s.heat} /><span className="text-text-muted">polled {timeAgo(s.lastPolledAt)}</span></span></div>)}
                {!d!.sources.length && <p className="jh-muted">None — find it below to have the agent read this employer's jobs directly.</p>}
              </div>
              {!e.atsType && <Button className="mt-3" size="sm" loading={busy === 'ats'} iconLeft={<Radar size={14} />} onClick={async () => {
                setBusy('ats');
                try {
                  const r = await jobHunterService.discoverAts(e.employerUid);
                  if (r.found) { toast.success(`Found ${r.type} board “${r.slug}” — polling it now`); await load(); onChanged(); } else toast.error(r.message ?? 'No careers feed found');
                } catch { /* toasted */ } finally { setBusy(null); }
              }}>Find careers feed</Button>}
            </section>
            <section className="jh-card overflow-hidden">
              <div className="jh-card-pad !pb-2"><div className="jh-card-title">Recent jobs</div></div>
              <table className="jh-table"><tbody>
                {d!.jobs.slice(0, 15).map((j) => <tr key={j.jobUid}><td className="max-w-[360px] truncate font-medium"><a href={j.url} target="_blank" rel="noreferrer" className="hover:text-primary">{j.title}</a></td><td><CountryTag cc={j.country} compact /></td><td className="text-text-muted">{timeAgo(j.firstSeenAt)}</td><td>{!j.isOpen && <Pill tone="danger">Closed</Pill>}</td></tr>)}
                {!d!.jobs.length && <tr><td className="jh-muted">No jobs stored yet.</td></tr>}
              </tbody></table>
            </section>
            {d!.applications.length > 0 && (
              <section className="jh-card jh-card-pad">
                <div className="jh-card-title">Matches here</div>
                <div className="mt-2 flex flex-wrap gap-2">{d!.applications.map((a) => <span key={a.applicationUid} className="jh-chip">#{a.ref} · {a.evaluation.fitScore} · {STATUS_META[a.status].label}</span>)}</div>
              </section>
            )}
            <section className="jh-card jh-card-pad">
              <Field label="Notes"><textarea className="jh-input" rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)} placeholder="Recruiter contacts, register entry, relocation package…" /></Field>
              {notes !== (e.notes ?? '') && <div className="mt-2 flex justify-end"><Button size="sm" loading={busy === 'notes'} onClick={() => act('notes', () => jobHunterService.updateEmployer(e.employerUid, { notes }), 'Notes saved')}>Save notes</Button></div>}
            </section>
          </div>
          <div className="jh-drawer-foot">
            {e.status !== 'watched' && <Button size="sm" loading={busy === 'watched'} iconLeft={<Eye size={14} />} onClick={() => act('watched', () => jobHunterService.updateEmployer(e.employerUid, { status: 'watched' }), 'Watching')}>Watch</Button>}
            {e.status !== 'paused' && <Button size="sm" variant="secondary" loading={busy === 'paused'} iconLeft={<CirclePause size={14} />} onClick={() => act('paused', () => jobHunterService.updateEmployer(e.employerUid, { status: 'paused' }), 'Paused')}>Pause</Button>}
            {e.status !== 'blocked' && <Button size="sm" variant="ghost" loading={busy === 'blocked'} iconLeft={<Ban size={14} />} onClick={() => act('blocked', () => jobHunterService.updateEmployer(e.employerUid, { status: 'blocked' }), 'Blocked — its jobs are filtered out')}>Block</Button>}
            <Button size="sm" variant="ghost" onClick={() => act('agency', () => jobHunterService.updateEmployer(e.employerUid, { isAgency: !e.isAgency }), e.isAgency ? 'No longer an agency' : 'Marked as agency')}>{e.isAgency ? 'Not an agency' : 'Mark as agency'}</Button>
          </div>
        </>
      )}
    </Drawer>
  );
}

export default function JhEmployers() {
  const [tab, setTab] = useState<Tab>('watched');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'recent' | 'proposed' | 'name'>('recent');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: JhEmployer[]; total: number; counts: Record<string, number> } | null>(null);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [probing, setProbing] = useState<string | null>(null);

  useEffect(() => { const t = window.setTimeout(() => setQuery(q.trim()), 300); return () => window.clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [tab, query, sort]);
  const load = useCallback(async () => {
    try {
      const r = await jobHunterService.employers({ status: tab === 'all' ? undefined : tab, q: query || undefined, sort, page, limit: 36 });
      setData({ rows: r.rows, total: r.total, counts: r.counts });
    } catch { setData({ rows: [], total: 0, counts: {} }); }
  }, [tab, query, sort, page]);
  useEffect(() => { void load(); }, [load]);
  useRunFinished(load);

  const probe = async (e: JhEmployer) => {
    setProbing(e.employerUid);
    try {
      const r = await jobHunterService.discoverAts(e.employerUid);
      if (r.found) toast.success(`Found ${r.type} board “${r.slug}” — polling it now`); else toast.error(r.message ?? 'No careers feed found');
      await load();
    } catch { /* toasted */ } finally { setProbing(null); }
  };

  const c = data?.counts ?? {};
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / 36));
  return (
    <JobHunterShell section="Employers" description="Who the agent watches, and why it thinks they sponsor"
      actions={<Button size="sm" iconLeft={<Plus size={14} />} onClick={() => setAdding(true)}>Watch employer</Button>}>
      <div className="jh-toolbar">
        <Segmented<Tab> value={tab} onChange={setTab} options={[
          { value: 'watched', label: 'Watched', n: c.watched ?? 0, icon: <Eye size={13} /> },
          { value: 'evidence', label: 'With evidence' },
          { value: 'seen', label: 'Seen', n: c.seen ?? 0 },
          { value: 'paused', label: 'Paused', n: c.paused ?? 0 },
          { value: 'blocked', label: 'Blocked', n: c.blocked ?? 0 },
          { value: 'all', label: 'All' },
        ]} />
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="jh-input pl-9" placeholder="Search employers" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <JhSelect size="sm" className="w-40" ariaLabel="Sort" value={sort} onChange={setSort}
          options={[{ value: 'recent' as const, label: 'Latest job first' }, { value: 'proposed' as const, label: 'Most matches' }, { value: 'name' as const, label: 'Name' }]} />
      </div>

      {data === null ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-surface-alt" />)}</div>
      ) : data.rows.length === 0 ? (
        <div className="jh-hero text-center">
          <Building2 className="mx-auto text-primary" size={28} />
          <div className="mt-3 text-[15px] font-semibold">No employers here yet</div>
          <p className="jh-muted mx-auto mt-1 max-w-md">Employers appear as the agent reads jobs. Watch one by hand, or load the starter set on Sources.</p>
          <Button className="mt-4" iconLeft={<Plus size={14} />} onClick={() => setAdding(true)}>Watch employer</Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.rows.map((e) => {
            const latest = e.sponsorshipEvidence[e.sponsorshipEvidence.length - 1];
            return (
              <article key={e.employerUid} className="jh-card jh-card-pad cursor-pointer transition hover:border-border-strong hover:shadow-elevated" onClick={() => setOpen(e.employerUid)}>
                <div className="flex items-start gap-3">
                  <EmployerAvatar name={e.name} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14.5px] font-semibold">{e.name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-text-muted"><CountryTag cc={e.country} compact />{e.lastJobAt && <span>last job {timeAgo(e.lastJobAt)}</span>}</div>
                  </div>
                  <Pill tone={e.status === 'watched' ? 'success' : e.status === 'blocked' ? 'danger' : e.status === 'paused' ? 'warning' : 'neutral'}>{e.status}</Pill>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {e.atsType ? <Pill tone="success" icon={<BadgeCheck size={12} />}>{e.atsType}</Pill>
                    : <button type="button" className="jh-pill bg-primary-soft text-primary hover:brightness-110" disabled={probing === e.employerUid} onClick={(ev) => { ev.stopPropagation(); void probe(e); }}>
                      {probing === e.employerUid ? <LoaderCircle size={12} className="animate-spin" /> : <Radar size={12} />}Find careers feed</button>}
                  <span className="jh-chip">{e.openJobs ?? 0} open</span>
                  {e.proposedCount > 0 && <span className="jh-chip">{e.proposedCount} match{e.proposedCount === 1 ? '' : 'es'}</span>}
                  {e.isAgency && <Pill tone="warning">Agency</Pill>}
                </div>
                {latest?.quote && <p className="jh-quote mt-3 line-clamp-2">“{latest.quote}”</p>}
              </article>
            );
          })}
        </div>
      )}
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-text-muted">Page {page} of {pages}</span>
          <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
      <AddEmployer open={adding} onClose={() => setAdding(false)} onSaved={() => void load()} />
      <EmployerDrawer uid={open} onClose={() => setOpen(null)} onChanged={() => void load()} />
    </JobHunterShell>
  );
}
